import React, { useEffect, useMemo, useState } from 'react';
import {
  Backdrop,
  Box,
  CircularProgress,
  Fab,
  Grid,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { isMobile } from 'react-device-detect';
import { KeyboardArrowUp } from '@mui/icons-material';
import styles from './styles';
import FeaturedWellGrid from '../featuredWellGrid';
import { reorderObjectKeys } from '../../../utils/objectHelpers';
import { HYDRAULICS_RUN_TYPES } from '../../../utils/constants';
import { REDUX_ACTIONS } from '../../../shared/config/constants';
import { fetchFeaturedWellDetails } from '../../../store/actions/hydraulicsLanding';
import FeaturedWellRunInputs from '../featuredWellRunInputs';
import FeaturedWellHydraulicsRunChart from '../featuredWellCharts/hydraulicsRunChart';
import FeaturedWellStrippingChart from '../featuredWellCharts/strippingsChart';
import ParametricAnalysisChart from '../featuredWellCharts/parametricAnalysisChart';
import FeaturedWellDisplacementChart from '../featuredWellCharts/displacementChart';
import FeaturedWellRolloverChart from '../featuredWellCharts/rolloverChart';
import { COLORS } from '../../../shared/config/colors';
import FeaturedWellSurveyCharts from '../featuredWellSurveys';

const keyNameMapping = {
  surveys: 'Surveys',
  geometries: 'Geometry',
  windows: 'Drilling Window',
  drillStrings: 'Drillstring',
  bits: 'Bit',
  drillFluids: 'Drilling Fluid'
};

const chartComponents = {
  hydraulicsRun: FeaturedWellHydraulicsRunChart,
  stripping: FeaturedWellStrippingChart,
  parametricAnalysis: ParametricAnalysisChart,
  displacement: FeaturedWellDisplacementChart,
  rollover: FeaturedWellRolloverChart,
  // add more mappings as needed
};

const Featured = () => {
  const classes = styles();
  const dispatch = useDispatch();
  const hydraulicsLanding = useSelector(state => state.hydraulicsLanding);
  const featuredWellRun = useSelector(state => state.hydraulicsLanding.featuredWellRun);
  const [expanded, setExpanded] = useState(null);
  const [chartType, setChartType] = useState(
    Object.values(HYDRAULICS_RUN_TYPES).find(
      item => item.typeKey === hydraulicsLanding.runType
    )
  );
  const ChartComponent = chartComponents[chartType?.typeKey];

  const chartWindow = useMemo(() => ({
    data: hydraulicsLanding?.data?.windows,
    units: hydraulicsLanding?.units?.windows
  }), [
    hydraulicsLanding?.data?.windows,
    hydraulicsLanding?.units?.windows
  ]);

  const handleChartTypeClick = (selectedChart) => {
    setChartType(selectedChart);
    dispatch({
      type: REDUX_ACTIONS.HYDRAULICS_LANDING_SET_FEATURED_WELL_RUN_TYPE,
      payload: selectedChart?.typeKey
    });
    const tabBar = document.getElementById('chart-types-select');
    if (tabBar && isMobile) {
      setTimeout(() => {
        tabBar.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 500);
    }
  };

  useEffect(() => {
    dispatch(fetchFeaturedWellDetails());
  }, []);

  const handleChange = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
    if (newExpanded && isMobile) {
      setTimeout(() => {
        handleScrollToAccordian(event, panel);
      }, 500);
    }
  };

  const handleScrollToAccordian = (event, newExpanded) => {
    const anchor = (event.target.ownerDocument || document).querySelector(
      `#back-to-${newExpanded || expanded}`,
    );

    if (anchor) {
      anchor.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    if (!isMobile) return;
    if (hydraulicsLanding.hydraulicsRunLoading) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [hydraulicsLanding.hydraulicsRunLoading]);

  return (
    <>
      {isMobile
        ? (
          <Backdrop
            sx={{
              color: COLORS.WHITE,
              zIndex: (theme) => theme.zIndex.drawer + 1,
            }}
            open={hydraulicsLanding.hydraulicsRunLoading}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
          )
        : null}
        <Box style={{
          width: '100%',
          backgroundColor: 'white',
          paddingBottom: isMobile ? '0px' : '40px',
        }}>
      <Box className={classes.featuredWrapperAccordion}>
        <Typography variant='h3' className={classes.hyraulicsSubtitle}>Your Data Is Your Competitive Advantage</Typography>
        {hydraulicsLanding?.loading
          ? (
            <Box className={classes.loading}>
              <CircularProgress />
            </Box>
            )
          : (
            <>
              {Object.entries(reorderObjectKeys(hydraulicsLanding?.data, keyNameMapping)).map(([key, value]) => {
                if (!keyNameMapping[key]) return null;
                if (Array.isArray(value) && value.length) {
                  return (
                    <Accordion
                      id={`back-to-${key}`}
                      key={key}
                      expanded={expanded === key}
                      onChange={handleChange(key)}
                      sx={{ scrollMarginTop: '80px' }}
                      TransitionProps={{
                        timeout: expanded === key ? 500 : 0
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1-content"
                        id="panel1-header"
                        sx={{ fontSize: '22px', fontWeight: 'bold' }}
                      >
                        {keyNameMapping[key]}
                      </AccordionSummary>
                      {expanded === key && (
                        <AccordionDetails>
                          {key === 'surveys'
                            ? (
                              <FeaturedWellSurveyCharts
                                data={value}
                                units={hydraulicsLanding?.units?.surveys}
                              />
                              )
                            : (
                              <FeaturedWellGrid
                                data={value}
                                gridKey={key}
                                units={hydraulicsLanding?.units}
                              />
                              )}
                        </AccordionDetails>
                      )}
                      {isMobile && expanded === key && value.length > 2 && (
                        <Fab size='medium' className={classes.accordianScrollUp} onClick={handleScrollToAccordian}>
                          <KeyboardArrowUp sx={{ fontSize: '40px' }} />
                        </Fab>
                      )}
                    </Accordion>
                  );
                }
                return null;
              })}
              {
                !hydraulicsLanding?.loading && featuredWellRun?.run_details
                  ? (
                    <FeaturedWellRunInputs
                      hydraulicsLanding={hydraulicsLanding}
                      featuredWellRun={featuredWellRun}

                      handleChartTypeClick={handleChartTypeClick}
                      chartType={chartType}
                      handleChange={handleChange}
                    />
                    )
                  : null
              }
            </>
            )}
      </Box>
      </Box>
      {!hydraulicsLanding.loading
        ? (
          <>
            {hydraulicsLanding?.hydraulicsRunLoading
              ? (
                  <Box className={classes.loading}>
                    <CircularProgress />
                  </Box>
                )
              : !featuredWellRun?.results || featuredWellRun?.results?.length === 0
                  ? (
                    <Typography variant="h5" textAlign="center">
                      No Data
                    </Typography>
                    )
                  : ChartComponent
                    ? (
                      <Grid className={classes.chartGrid}>
                        <Grid className={classes.chartContainer}>
                          <ChartComponent
                            data={featuredWellRun}
                            window={chartWindow}
                            units={hydraulicsLanding?.units}
                            hydraulicsLanding={hydraulicsLanding}
                          />
                        </Grid>
                      </Grid>
                      )
                    : null}
          </>
          )
        : null}
    </>
  );
};

export default React.memo(Featured);
